import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from 'components/ui';
import {
  AtlasGqlOrganizationRelationshipScope,
  useGetBulkLikelyForOrganizationsQuery,
  useGetOrganizationRelationshipsQuery,
} from 'types/atlas-graphql';
import { notEmpty } from 'utils/types';
import { useTaskListFilters } from 'utils/useTaskListFilters';

interface UseOrganizationRelationshipsArgs {
  scope?: AtlasGqlOrganizationRelationshipScope;
  skip?: boolean;
  types?: ('vendor' | 'delivery')[];
}

export function useOrganizationRelationships({
  scope = AtlasGqlOrganizationRelationshipScope.RelationshipOwner,
  skip,
  types,
}: UseOrganizationRelationshipsArgs = {}) {
  const { data, error, loading } = useGetOrganizationRelationshipsQuery({
    skip,
    variables: { scope, types },
  });

  const relationships = data?.organizationRelationships?.records;
  let errors = data?.organizationRelationships?.errors;

  if (!errors && error) {
    errors = [{ message: error.message, __typename: 'UserInputError' }];
  }

  if (loading) {
    return { loading, relationships: [] };
  }

  return { loading, errors, relationships };
}

interface UseOrganizationVendorRelationshipsArgs {
  skip?: boolean;
}

export function useOrganizationVendorRelationships({
  skip,
}: UseOrganizationVendorRelationshipsArgs = {}) {
  const { errors, loading, relationships } = useOrganizationRelationships({
    skip,
    scope: AtlasGqlOrganizationRelationshipScope.RelationshipOwner,
    types: ['vendor'],
  });

  const vendors = relationships?.map(r => r.relationshipPartner).filter(notEmpty) ?? [];
  const errorsString = errors?.map(e => e.message).join('. ');

  return { errors, errorsString, loading, vendors };
}

/**
 * Gets the list of Organizations that are currently vendors to be used for Vendor selection.
 */
export function useVendorOrgRelationshipOptions() {
  const { t } = useTranslation();
  const message = useMessage();
  const { errorsString, loading, vendors } = useOrganizationVendorRelationships();

  useEffect(() => {
    if (!loading && errorsString) {
      message.warning(
        t('vendors.unableToLoadVendors', 'Unable to load vendors. {{errorsString}}', {
          errorsString,
        }),
        8
      );
    }
  }, [errorsString, loading, message, t]);

  const vendorOptions = useMemo(
    () =>
      vendors.map(v => ({
        label: v.name,
        text: v.name,
        value: v.id,
      })),
    [vendors]
  );

  return { loading, vendorOptions };
}

/**
 * Gets the list of Organizations that are currently assigned vendors to be used for Task filtering.
 */
export function useVendorOrgFilterOptions() {
  const { t } = useTranslation();
  const message = useMessage();
  const { filters, loading, error } = useTaskListFilters();
  const vendors = useMemo(() => filters.vendorOrganization ?? [], [filters]);

  useEffect(() => {
    if (!loading && error) {
      message.warning(
        t('vendors.unableToLoadVendors', 'Unable to load vendors. {{errorsString}}', {
          errorsString: error.message,
        }),
        8
      );
    }
  }, [error, loading, message, t]);

  const vendorOptions = useMemo(
    () =>
      vendors.map(v => ({
        label: v.name,
        text: v.name,
        value: v.name,
      })),
    [vendors]
  );

  return { loading, vendorOptions };
}

interface UseOrganizationDeliveryRelationshipsArgs {
  skip?: boolean;
}
interface UseLikelyForOrganizationDeliveryRelationshipsArgs {
  skip?: boolean;
  assetIds?: string[];
}

/**
 * Gets a list of organizations that this organization is allowed to deliver to.
 * @param skip - Whether to skip the query.
 */
export function useOrganizationDeliveryCustomers({
  skip,
}: UseOrganizationDeliveryRelationshipsArgs = {}) {
  const { errors, loading, relationships } = useOrganizationRelationships({
    skip,
    scope: AtlasGqlOrganizationRelationshipScope.RelationshipPartner,
    types: ['delivery'],
  });

  return useMemo(() => {
    return {
      loading,
      errors,
      errorsString: errors?.map(e => e.message).join('. '),
      deliveryCustomers: relationships?.map(r => r.relationshipOwner).filter(notEmpty) ?? [],
    };
  }, [errors, loading, relationships]);
}

/**
 * Gets a list of organizations that the given asset has previously been delivered to
 * Therefore making the organizations "likely" options for future deliveries
 * @param skip - Whether to skip the query.
 */
export function useLikelyOrganizationDeliveryCustomers({
  assetIds,
  skip,
}: UseLikelyForOrganizationDeliveryRelationshipsArgs = {}) {
  const { data, error, loading, refetch } = useGetBulkLikelyForOrganizationsQuery({
    skip: skip || !assetIds?.length,
    variables: {
      assetIds: assetIds ?? [],
    },
  });

  // useMemo here to reduce the number of times this is re-calculated
  // in the case where the hook is repeatedly called without any dependency data changing
  return useMemo(() => {
    const relationships = data?.bulkLikelyForOrganizations ?? [];
    return {
      error: error,
      errorString: error?.message,
      loading,
      deliveryCustomers: relationships,
      refetch,
    };
  }, [data, error, loading, refetch]);
}

interface UseDeliveryOrgOptionsArgs {
  assetIds?: string[];
  valueKey?: 'id' | 'name';
  skip?: boolean;
  sort?: boolean;
}

export function useDeliveryCustomerOrgOptions({
  assetIds,
  valueKey = 'id',
  skip,
  sort,
}: UseDeliveryOrgOptionsArgs) {
  const { t } = useTranslation();
  const message = useMessage();
  const { errorsString, loading, deliveryCustomers } = useOrganizationDeliveryCustomers({
    skip,
  });

  const {
    loading: likelyLoading,
    errorString: likelyErrors,
    deliveryCustomers: likelyCustomers,
    refetch: likelyRefetch,
  } = useLikelyOrganizationDeliveryCustomers({ assetIds, skip });

  useEffect(() => {
    if (!loading && errorsString) {
      message.warning(
        t(
          'vendors.unableToLoadDeliveryOrganizations',
          'Unable to load delivery organizations. {{errorsString}}',
          { errorsString }
        ),
        8
      );
    }
  }, [errorsString, loading, message, t]);

  useEffect(() => {
    if (assetIds) {
      likelyRefetch();
    }
    // We only want to refetch when assetIds changes AND if assetIds is non-empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetIds]);

  useEffect(() => {
    if (!likelyLoading && likelyErrors) {
      message.warning(
        t(
          'vendors.unableToLoadLikelyOrganizations',
          'Unable to load likely organizations. {{likelyErrors}}',
          { likelyErrors }
        ),
        8
      );
    }
  }, [likelyErrors, likelyLoading, message, t]);

  const deliveryCustomerOptions = useMemo(() => {
    const temp = deliveryCustomers.map(d => ({
      label: d.name,
      text: d.name,
      value: valueKey === 'name' ? d.name : d.id,
    }));

    if (sort) {
      temp.sort((a, b) => a.label.localeCompare(b.label));
    }

    return temp;
  }, [deliveryCustomers, sort, valueKey]);

  const likelyCustomerOptions = useMemo(() => {
    const temp = likelyCustomers.map(d => ({
      label: d.name,
      text: d.name,
      value: valueKey === 'name' ? d.name : d.id,
    }));

    if (sort) {
      temp.sort((a, b) => a.label.localeCompare(b.label));
    }

    return temp;
  }, [likelyCustomers, sort, valueKey]);

  return { loading: likelyLoading || loading, deliveryCustomerOptions, likelyCustomerOptions };
}
